import React, { useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home, Users, MessageSquare, Sparkles, User, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";
import { readIntent } from "@/lib/intent";

const NAV = [
  { to: "/app/home", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/app/match", label: "Meet", icon: Users, testid: "nav-match" },
  { to: "/app/discover", label: "AI", icon: Sparkles, testid: "nav-discover" },
  { to: "/app/chats", label: "Chats", icon: MessageSquare, testid: "nav-chats" },
  { to: "/app/profile", label: "Settings", icon: User, testid: "nav-profile" },
];

export default function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Landing-page intent: if the visitor picked a card before signing in, route
  // them into that flow exactly as the Home option buttons would. The saved
  // value is deleted on read (used, expired or invalid) so it fires once.
  useEffect(() => {
    const type = readIntent();
    if (!type) return;
    navigate(type === "human" ? "/app/match" : "/app/discover", { replace: true });
  }, [navigate]);

  if (!user) return null;
  const isMember = user?.membership?.status === "member" || user?.plan === "premium";

  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise md:flex">
      <aside className="hidden w-[260px] shrink-0 flex-col border-r border-[#2B241B] bg-[#12100E] md:flex">
        <div className="px-6 py-6"><span className="font-display text-2xl text-gold-gradient">{BRAND.name}</span></div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`${n.testid}-desktop`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#24201C] text-[#D4AF37]" : "text-[#C8C2B5] hover:bg-[#1A1714]"}`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
          <NavLink to="/app/membership" data-testid="nav-membership-desktop"
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-sm ${isActive ? "bg-[#24201C] text-[#D4AF37]" : "text-[#C8C2B5] hover:bg-[#1A1714]"}`}>
            <Crown className="h-5 w-5" /> Membership
          </NavLink>
        </nav>
        <div className="border-t border-[#2B241B] p-4">
          <div className="flex items-center gap-3">
            {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#24201C] text-xs text-[#D4AF37]">{(user.name || "U")[0]}</div>}
            <div className="min-w-0"><p className="truncate text-sm text-[#F4F1EA]">{user.name}</p><p className="truncate text-xs text-[#8C8578]">{isMember ? "Member" : "Early access"}</p></div>
          </div>
        </div>
      </aside>

      <main className="min-h-screen flex-1 pb-20 md:pb-0"><Outlet /></main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-stretch border-t border-[#2B241B] bg-[#12100E]/95 backdrop-blur safe-bottom md:hidden">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`${n.testid}-mobile`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] ${isActive ? "text-[#D4AF37]" : "text-[#8C8578]"}`}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
